import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FINANCIAL_API, API, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const CONSOLIDATED = '__CONSOLIDATED__';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtSigned = (n: number) =>
  n < 0 ? `(${fmt(Math.abs(n))})` : fmt(n);

export default function Conversion() {
  const { entity: globalEntity } = useGlobalEntity();
  const [entities, setEntities] = useState<any[]>([]);
  const [entity, setEntity] = useState('');
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [rules, setRules] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [consolidated, setConsolidated] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [convExceptions, setConvExceptions] = useState<any[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const navigate = useNavigate();

  const isConsolidated = entity === CONSOLIDATED;

  useEffect(() => {
    authFetch(`${FINANCIAL_API}/entities`)
      .then(r => r.json())
      .then(rows => {
        setEntities(rows);
        if (globalEntity) {
          setEntity(globalEntity);
        } else {
          const needsConv = rows.find((e: any) => e.local_framework && e.local_framework !== e.reporting_framework);
          if (needsConv) setEntity(needsConv.entity_code);
          else if (rows.length > 0) setEntity(rows[0].entity_code);
        }
      });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (globalEntity && globalEntity !== entity) setEntity(globalEntity);
  }, [globalEntity]); // eslint-disable-line

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/available-periods`
      : `${FINANCIAL_API}/available-periods?entity_code=${entity}`;
    authFetch(url)
      .then(r => r.json())
      .then(d => {
        setPeriods(d.periods || []);
        if (d.periods?.length) setPeriod(d.periods[d.periods.length - 1]);
      });
  }, [entity, isConsolidated]);

  useEffect(() => {
    if (!entity || isConsolidated) {
      setRules([]);
      return;
    }
    authFetch(`${FINANCIAL_API}/conversion/rules?entity_code=${entity}`)
      .then(r => r.json())
      .then(d => setRules(d.rules || []))
      .catch(() => setRules([]));
  }, [entity, isConsolidated]);

  const loadData = useCallback(() => {
    if (!entity || !period) return;
    setLoading(true);

    if (isConsolidated) {
      authFetch(`${FINANCIAL_API}/conversion/consolidated-impact?period=${period}`)
        .then(r => r.json())
        .then(d => { setConsolidated(d); setSummary(null); setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      Promise.all([
        authFetch(`${FINANCIAL_API}/conversion/summary?entity_code=${entity}&period=${period}`).then(r => r.json()),
        authFetch(`${FINANCIAL_API}/conversion/consolidated-impact?period=${period}`).then(r => r.json()),
      ]).then(([s, c]) => {
        setSummary(s);
        setConsolidated(c);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [entity, period, isConsolidated]);

  useEffect(() => { loadData(); }, [loadData]);

  // Fetch conversion exceptions
  const loadConvExceptions = useCallback(() => {
    authFetch(`${API}?limit=500`)
      .then(r => r.json())
      .then(data => {
        const items = (data.items || data || []).filter(
          (e: any) => e.exception_type === 'framework_conversion'
        );
        setConvExceptions(items);
      })
      .catch(() => setConvExceptions([]));
  }, []);

  useEffect(() => { loadConvExceptions(); }, [loadConvExceptions]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleDetect = async () => {
    if (!period) return;
    setDetecting(true);
    try {
      const url = entity && !isConsolidated
        ? `${API}/detect-conversion?period=${period}&entity_code=${entity}`
        : `${API}/detect-conversion?period=${period}`;
      const resp = await authFetch(url, { method: 'POST' });
      const result = await resp.json();
      const detected = result.total_detected ?? result.detected ?? 0;
      const skipped = result.total_skipped ?? result.skipped_duplicate ?? 0;
      setToast(`Detected ${detected} new conversion exception${detected !== 1 ? 's' : ''}${skipped ? ` (${skipped} already existed)` : ''}`);
      loadConvExceptions();
    } catch {
      setToast('Detection failed -- check backend logs');
    } finally {
      setDetecting(false);
    }
  };

  const entityInfo = entities.find(e => e.entity_code === entity);
  const needsConversion = entityInfo && entityInfo.local_framework && entityInfo.local_framework !== entityInfo.reporting_framework;

  // Derived from summary
  const totalJes = summary?.conversion_jes || 0;
  const rulesApplied = summary?.rules_applied || [];
  const impact = summary?.impact || {};
  const plImpact = impact.net_income_impact || 0;
  const bsImpact = impact.total_assets_impact || 0;

  // Consolidated totals
  const consolTotalJes = consolidated?.total_conversion_jes || 0;
  const consolEntities = consolidated?.entities || [];
  const consolTotalPL = consolEntities.reduce((s: number, e: any) => s + (e.impact?.net_income_impact || 0), 0);
  const consolTotalBS = consolEntities.reduce((s: number, e: any) => s + (e.impact?.total_assets_impact || 0), 0);

  // Table columns for conversion JEs
  const jeColumns: Column[] = [
    { key: 'rule_id', label: 'Rule', width: 90 },
    { key: 'area', label: 'Area' },
    { key: 'standard_citation', label: 'Standard', width: 180 },
    { key: 'je_count', label: 'JEs', align: 'right', width: 50 },
    { key: 'total_debits', label: 'Debits', align: 'right', width: 130, render: (v: number) => fmt(v) },
    { key: 'total_credits', label: 'Credits', align: 'right', width: 130, render: (v: number) => fmt(v) },
  ];

  // Consolidated impact columns
  const consolColumns: Column[] = [
    { key: 'entity_name', label: 'Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'local_framework', label: 'Local', width: 80 },
    { key: 'reporting_framework', label: 'Target', width: 80 },
    { key: 'conversion_jes', label: 'JEs', align: 'right', width: 60 },
    { key: 'ni_impact', label: 'NI Impact', align: 'right', width: 130, render: (_: any, row: any) => {
      const v = row.impact?.net_income_impact || 0;
      return <span style={{ color: v < 0 ? 'var(--negative)' : v > 0 ? 'var(--positive)' : 'var(--text-hint)' }}>{fmtSigned(v)}</span>;
    }},
    { key: 'assets_impact', label: 'Assets Impact', align: 'right', width: 130, render: (_: any, row: any) => fmtSigned(row.impact?.total_assets_impact || 0) },
  ];

  // Exception columns
  const exColumns: Column[] = [
    { key: 'id', label: 'ID', width: 50 },
    { key: 'title', label: 'Rule / Topic', render: (v: string, row: any) => (v || row.description || '').replace('Framework Conversion: ', '') },
    { key: 'entity_code', label: 'Entity', width: 100, render: (v: string, row: any) => row.jurisdiction || v || '\u2014' },
    { key: 'amount', label: 'Amount', align: 'right', width: 120, render: (v: number) => fmt(v || 0) },
    { key: 'severity', label: 'Severity', align: 'center', width: 80, render: (v: string) => {
      const colors: Record<string, string> = { critical: 'var(--negative)', high: 'var(--warning)', medium: 'var(--navy-700)', low: 'var(--positive)' };
      return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: `${colors[(v || '').toLowerCase()] || 'var(--text-hint)'}15`, color: colors[(v || '').toLowerCase()] || 'var(--text-hint)', textTransform: 'uppercase' }}>{v || '\u2014'}</span>;
    }},
    { key: 'status', label: 'Status', align: 'center', width: 90, render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>{(v || '').replace(/_/g, ' ')}</span> },
  ];

  return (
    <PageLayout
      title="GAAP/IFRS Conversion Dashboard"
      subtitle="Framework conversion layer between local entity books and consolidated reporting"
    >
      {/* Controls */}
      <FilterBar filters={[
        {
          key: 'entity', label: 'Entity', type: 'select', value: entity,
          onChange: setEntity,
          options: [
            ...entities.map(e => ({
              value: e.entity_code,
              label: `${e.entity_name} (${e.functional_currency})${e.local_framework && e.local_framework !== e.reporting_framework ? ` [${e.local_framework} -> ${e.reporting_framework}]` : e.local_framework ? ` [${e.local_framework}]` : ''}`,
            })),
            { value: CONSOLIDATED, label: 'Consolidated (USD)' },
          ],
        },
        {
          key: 'period', label: 'Period', type: 'select', value: period,
          onChange: setPeriod,
          options: periods.map(p => ({ value: p, label: p })),
        },
      ]} />

      {loading && <div style={{ fontSize: 12, color: 'var(--text-hint)', marginBottom: 12 }}>Loading...</div>}

      {/* Entity Framework Status */}
      {!isConsolidated && entityInfo && (
        <div style={{
          marginBottom: 20, padding: '14px 20px', borderRadius: 10,
          background: needsConversion ? 'var(--warning-bg)' : 'var(--positive-bg)',
          border: `1px solid ${needsConversion ? 'var(--warning-border)' : 'var(--positive-border)'}`,
          display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div>
            <span style={{ fontSize: 11, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Local Framework</span>
            <div style={{ fontSize: 16, fontWeight: 700, color: needsConversion ? 'var(--warning)' : 'var(--positive)', marginTop: 2 }}>
              {entityInfo.local_framework || 'Not Set'}
            </div>
          </div>
          <span style={{ fontSize: 20, color: 'var(--text-hint)' }}>{'\u2192'}</span>
          <div>
            <span style={{ fontSize: 11, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Reporting Framework</span>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-800)', marginTop: 2 }}>
              {entityInfo.reporting_framework || 'Not Set'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <span style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: needsConversion ? 'var(--warning-bg)' : 'var(--positive-bg)',
              color: needsConversion ? 'var(--warning)' : 'var(--positive)',
            }}>
              {needsConversion ? 'Conversion Required' : 'No Conversion Needed'}
            </span>
          </div>
        </div>
      )}

      {/* No conversion needed */}
      {!isConsolidated && !needsConversion && (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 14,
          background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)',
        }}>
          This entity reports under {entityInfo?.local_framework || 'the same framework'} locally and for consolidation. No conversion adjustments needed.
        </div>
      )}

      {/* Summary Stats */}
      {!isConsolidated && needsConversion && summary && (
        <KpiGrid columns={4} items={[
          { label: 'Conversion JEs', value: String(totalJes), color: 'var(--warning)' },
          { label: 'Rules Applied', value: String(rulesApplied.length), color: 'var(--navy-800)' },
          { label: 'P&L Impact', value: fmtSigned(plImpact), color: plImpact < 0 ? 'var(--negative)' : 'var(--positive)' },
          { label: 'Assets Impact', value: fmtSigned(bsImpact), color: 'var(--navy-800)' },
        ]} />
      )}

      {/* Rules Detail Table (from summary) */}
      {!isConsolidated && needsConversion && rulesApplied.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 8 }}>
            Conversion Journal Entries -- {period}
          </div>
          <DataTable columns={jeColumns} rows={rulesApplied} rowKey="rule_id" />
        </div>
      )}

      {/* Full Rules Reference */}
      {!isConsolidated && needsConversion && rules.length > 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>
            Applicable Conversion Rules ({entityInfo?.local_framework} {'\u2192'} {entityInfo?.reporting_framework}) -- {rules.length} rules
          </div>
          <div style={{ padding: 16 }}>
            {rules.map((r: any) => (
              <div key={r.rule_id} style={{
                marginBottom: 12, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)',
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'var(--warning)', fontWeight: 700 }}>{r.rule_id}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-900)' }}>{r.area}</span>
                  {r.standard_citation && (
                    <span style={{ fontSize: 10, color: 'var(--text-hint)', fontVariantNumeric: 'tabular-nums' }}>{r.standard_citation}</span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                  <div>
                    <span style={{ color: 'var(--warning)', fontWeight: 600 }}>IFRS: </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{r.ifrs}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--navy-800)', fontWeight: 600 }}>GAAP: </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{r.gaap}</span>
                  </div>
                </div>
                {r.accounts_affected && r.accounts_affected.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-hint)' }}>
                    Accounts: {r.accounts_affected.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consolidated Impact */}
      {consolEntities.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 8 }}>
            Consolidated Conversion Impact -- {period}
          </div>
          <DataTable columns={consolColumns} rows={consolEntities} rowKey="entity_code" />
        </div>
      )}

      {/* Conversion Exceptions Requiring Review */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)' }}>
            Conversion Exceptions Requiring Review
            {convExceptions.length > 0 && (
              <span style={{
                marginLeft: 8, padding: '2px 8px', borderRadius: 10,
                fontSize: 11, fontWeight: 600,
                background: 'rgba(239,68,68,0.08)', color: 'var(--negative)',
              }}>
                {convExceptions.length}
              </span>
            )}
          </div>
          <ActionButton
            label={detecting ? 'Scanning...' : 'Detect Conversion Differences'}
            onClick={handleDetect}
            disabled={detecting || !period}
            variant="secondary"
            size="sm"
          />
        </div>

        {convExceptions.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>
            No conversion exceptions detected. Click "Detect Conversion Differences" to scan for
            framework differences requiring human judgment.
          </div>
        ) : (
          <DataTable
            columns={exColumns}
            rows={convExceptions}
            rowKey="id"
            onRowClick={() => navigate('/tasks')}
          />
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          padding: '10px 20px', borderRadius: 10,
          background: toast.includes('failed') ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
          color: toast.includes('failed') ? 'var(--negative)' : 'var(--positive)',
          border: `1px solid ${toast.includes('failed') ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
          fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        }}>
          {toast}
        </div>
      )}

      {/* Impact Summary Banner */}
      {summary && needsConversion && (
        <div style={{
          padding: '12px 20px', borderRadius: 10,
          background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)',
          fontSize: 13, fontWeight: 600, color: 'var(--warning)',
          display: 'flex', gap: 24, flexWrap: 'wrap',
        }}>
          <span>
            NI Adjustment: {fmtSigned(plImpact)} {entityInfo?.functional_currency}
          </span>
          <span style={{ color: 'var(--text-hint)' }}>|</span>
          <span>
            Assets: {fmtSigned(bsImpact)} {entityInfo?.functional_currency}
          </span>
          <span style={{ color: 'var(--text-hint)' }}>|</span>
          <span>
            Equity: {fmtSigned(impact.total_equity_impact || 0)} {entityInfo?.functional_currency}
          </span>
          <span style={{ color: 'var(--text-hint)' }}>|</span>
          <span>
            Liabilities: {fmtSigned(impact.total_liabilities_impact || 0)} {entityInfo?.functional_currency}
          </span>
        </div>
      )}
    </PageLayout>
  );
}
